import { useEffect, useMemo } from 'react';

import { CuroApplicationVariables } from 'enums/curo/Variables';
import { getApplicationSelector } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import { saveSelectedOffer } from 'thunks';

import { LoanOfferVariant } from './SelectLoanOffer.types';
import SelectLoanOfferStepView from './SelectLoanOfferStepView';
import {
  formatLoanOfferDataToTableData,
  ILoanOfferItem,
  IRefinanceLoanOfferItem,
} from './constants';
import { useLoanOfferConfig } from './useLoanOfferConfig';

interface ISelectLoanOfferStepProps {
  onStepComplete: () => void;
  variant: LoanOfferVariant;
  onCancel?: () => void;
}

const SelectLoanOfferStep = ({ onStepComplete, variant, onCancel }: ISelectLoanOfferStepProps) => {
  const application = useAppSelector(getApplicationSelector)!;
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const trackEvent = useGoogleAnalytics();

  const offerExpirationDate = application.variables[
    CuroApplicationVariables.OfferExpirationDate
  ] as string;

  const loanOfferConfig = useLoanOfferConfig({
    variant,
    application,
    onCancel,
  });

  useEffect(() => {
    trackEvent(loanOfferConfig.analyticsEvent);
  }, []);

  const getLoanOfferDetails = (loanOffers: ILoanOfferItem | IRefinanceLoanOfferItem) => {
    const [loanOfferId, loanOfferDetails] = Object.entries(loanOffers)?.[0];
    const { productName, loanOfferData } = loanOfferDetails;

    const tableData = formatLoanOfferDataToTableData(
      loanOfferData,
      application.variables[CuroApplicationVariables.OrigGpsLoanOrAccountNumber] as string,
    );

    return {
      tableData,
      productName,
      loanOfferId,
    };
  };

  const loanOffers = useMemo(
    () => loanOfferConfig.parser(application.variables),
    [application.variables, loanOfferConfig.parser],
  );

  const { loanOfferId, tableData, productName } = getLoanOfferDetails(loanOffers);

  const handleAcceptOffer = async (offerId: string) => {
    await dispatchWithUnwrap(
      saveSelectedOffer({
        applicationId: application.id,
        selectedOffer: {
          selectedOfferId: offerId,
        },
        ...loanOfferConfig.saveOptions,
      }),
    );

    onStepComplete();
  };

  const actions = useMemo(
    () => [
      {
        label: 'Accept My Offer',
        kind: 'primary' as const,
        onClick: async () => {
          await handleAcceptOffer(loanOfferId);
        },
      },
      ...(loanOfferConfig.secondaryActions ?? []),
    ],
    [loanOfferConfig.secondaryActions],
  );

  return (
    <SelectLoanOfferStepView
      title={loanOfferConfig.title}
      productName={productName}
      tableProps={{
        data: tableData,
        rows: loanOfferConfig.tableRows,
        columns: loanOfferConfig.tableColumns,
      }}
      offerExpirationDate={offerExpirationDate}
      actions={actions}
      disclaimer={loanOfferConfig.disclaimer}
    />
  );
};

export default SelectLoanOfferStep;
